/**
 * AgentFileEditor — orchestrator for the middle editor pane.
 *
 * INTEGRATION CONTRACT (Agent 3 implements this. ScratchpadPage.tsx imports
 * this component and will not change.):
 *
 * Props:
 *   selectedFileId: string | null
 *     - The ID of the agent file currently selected in the tree.
 *     - null when nothing is selected.
 *   onDirtyChange: (dirty: boolean) => void
 *     - Called whenever the editor transitions between clean and dirty state.
 *     - The page uses this to show the unsaved-changes guard in AgentFileTree.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type { Database } from "@/integrations/supabase/types";
import { formatDistanceToNow } from "date-fns";
import { Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAgentFiles, useSaveAgentFile, useSoftDeleteAgentFile } from "@/hooks/useAgentFiles";
import { supabase } from "@/integrations/supabase/client";
import { StaleVersionError } from "@/types/agent-skills";
import type { AgentFile, Frontmatter } from "@/types/agent-skills";
import { FrontmatterForm } from "./FrontmatterForm";
import { MarkdownBodyEditor } from "./MarkdownBodyEditor";
import { SaveBar } from "./SaveBar";
import { StaleVersionDialog } from "./StaleVersionDialog";
import { HistoryDrawer } from "./HistoryDrawer";
import { skillFrontmatterSchema, agentFrontmatterSchema } from "./FrontmatterForm.schema";

interface AgentFileEditorProps {
  selectedFileId: string | null;
  onDirtyChange: (dirty: boolean) => void;
  /** Called after the currently-open file is deleted so the parent can clear the selection. */
  onDeleted?: () => void;
}

/**
 * Key-order-independent deep equality.
 * Handles plain objects (compares sorted keys recursively), arrays, and primitives.
 * Needed because Postgres serializes JSONB with alphabetical key order while the
 * FrontmatterForm builds objects in declaration order — a pure JSON.stringify diff
 * would report a false positive dirty state on initial load for skill files.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a as object).sort();
    const bKeys = Object.keys(b as object).sort();
    if (aKeys.length !== bKeys.length) return false;
    if (!aKeys.every((k, i) => k === bKeys[i])) return false;
    return aKeys.every((k) =>
      deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
    );
  }
  return false;
}

// Shorten a UUID to its first 8 chars for fallback display.
function shortUuid(uuid: string | null): string {
  if (!uuid) return "unknown";
  return uuid.slice(0, 8);
}

// Validate frontmatter against the appropriate schema.
function hasValidationErrors(file: AgentFile, frontmatter: Frontmatter): boolean {
  const schema = file.kind === "agent" ? agentFrontmatterSchema : skillFrontmatterSchema;
  const result = schema.safeParse({
    name: frontmatter.name,
    description: frontmatter.description,
    trigger_type: frontmatter.trigger_type,
    trigger_rule: frontmatter.trigger_rule,
  });
  return !result.success;
}

export function AgentFileEditor({
  selectedFileId,
  onDirtyChange,
  onDeleted,
}: AgentFileEditorProps) {
  const { rows, isLoading } = useAgentFiles();
  const saveFile = useSaveAgentFile();
  const softDelete = useSoftDeleteAgentFile();

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Version-history drawer state.
  const [historyOpen, setHistoryOpen] = useState(false);

  // The file currently loaded in the editor.
  const file: AgentFile | undefined = useMemo(
    () => (selectedFileId ? rows.find((r) => r.id === selectedFileId) : undefined),
    [rows, selectedFileId]
  );

  // Track the version that was loaded so we can pass it as expectedVersion on save.
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null);

  // Local draft state.
  const [draftFrontmatter, setDraftFrontmatter] = useState<Frontmatter | null>(null);
  const [draftBody, setDraftBody] = useState<string | null>(null);

  // Author display name (looked up from profiles).
  const [authorName, setAuthorName] = useState<string | null>(null);

  // Stale version dialog state.
  const [staleDialogOpen, setStaleDialogOpen] = useState(false);
  const [staleOtherAuthor, setStaleOtherAuthor] = useState("another user");
  const [staleTheirVersion, setStaleTheirVersion] = useState(0);

  // When the selected file changes, reset draft state to the loaded file's content.
  useEffect(() => {
    if (!file) {
      setLoadedVersion(null);
      setDraftFrontmatter(null);
      setDraftBody(null);
      setAuthorName(null);
      return;
    }
    setLoadedVersion(file.current_version);
    setDraftFrontmatter(file.frontmatter);
    setDraftBody(file.body);
    setHistoryOpen(false);
    // Only reset when a different file is selected, not on every field update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id]);

  // Fetch the author display name from profiles.
  useEffect(() => {
    if (!file?.updated_by) {
      setAuthorName(null);
      return;
    }
    supabase
      .from("profiles")
      .select("display_name")
      .eq("user_id" as keyof Database["public"]["Tables"]["profiles"]["Row"], file.updated_by)
      .maybeSingle()
      .then(({ data }) => {
        if (data && "display_name" in data && data.display_name) {
          setAuthorName(data.display_name as string);
        } else {
          setAuthorName(shortUuid(file.updated_by));
        }
      });
  }, [file?.updated_by]);

  // Compute dirty state by comparing drafts against the loaded file.
  // Uses deepEqual (key-order-independent) for frontmatter to avoid false positives when
  // Postgres JSONB key order differs from the FrontmatterForm's declaration order.
  const isDirty = useMemo(() => {
    if (!file || draftFrontmatter === null || draftBody === null) return false;
    const frontmatterChanged = !deepEqual(draftFrontmatter, file.frontmatter);
    const bodyChanged = draftBody !== file.body;
    return frontmatterChanged || bodyChanged;
  }, [file, draftFrontmatter, draftBody]);

  // Notify parent whenever dirty state changes.
  const prevDirtyRef = useRef(false);
  useEffect(() => {
    if (isDirty !== prevDirtyRef.current) {
      prevDirtyRef.current = isDirty;
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  // Determine if the current draft has validation errors.
  const validationErrors = useMemo(() => {
    if (!file || !draftFrontmatter) return false;
    return hasValidationErrors(file, draftFrontmatter);
  }, [file, draftFrontmatter]);

  const handleFrontmatterChange = useCallback((next: Frontmatter) => {
    setDraftFrontmatter(next);
  }, []);

  const handleBodyChange = useCallback((next: string) => {
    setDraftBody(next);
  }, []);

  async function handleDelete() {
    if (!file) return;
    await softDelete.mutateAsync(file.id);
    setDeleteConfirmOpen(false);
    onDeleted?.();
  }

  async function handleSave(note: string) {
    if (!file || draftFrontmatter === null || draftBody === null || loadedVersion === null) return;

    try {
      await saveFile.mutateAsync({
        id: file.id,
        expectedVersion: loadedVersion,
        frontmatter: draftFrontmatter,
        body: draftBody,
        note: note || undefined,
      });
      // On success, bump the loaded version so subsequent saves use the new version.
      setLoadedVersion(loadedVersion + 1);
    } catch (err) {
      if (err instanceof StaleVersionError) {
        // Determine who saved it and what their version is from the current DB row.
        const freshRow = rows.find((r) => r.id === file.id);
        if (freshRow) {
          setStaleTheirVersion(freshRow.current_version);
          setStaleOtherAuthor(
            freshRow.updated_by ? shortUuid(freshRow.updated_by) : "another user"
          );
        } else {
          setStaleTheirVersion(loadedVersion + 1);
          setStaleOtherAuthor("another user");
        }
        setStaleDialogOpen(true);
      }
      // Non-stale errors are handled by useSaveAgentFile's onError (toast).
    }
  }

  function handleReload() {
    // Reload resets local draft to whatever the cache has (which was refetched after the error).
    if (!file) return;
    setLoadedVersion(file.current_version);
    setDraftFrontmatter(file.frontmatter);
    setDraftBody(file.body);
    setStaleDialogOpen(false);
  }

  // ── Render states ──────────────────────────────────────────────────────────

  if (!selectedFileId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a file to start editing
      </div>
    );
  }

  if (isLoading || !file || draftFrontmatter === null || draftBody === null) {
    return (
      <div className="flex flex-col h-full">
        {/* Toolbar skeleton */}
        <div className="h-12 border-b border-border px-4 flex items-center gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
        {/* Frontmatter form skeleton */}
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-9 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-20 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-48" />
          </div>
        </div>
        {/* Body skeleton */}
        <div className="flex-1 px-4 pb-4">
          <Skeleton className="h-full w-full min-h-[400px] rounded-md" />
        </div>
        {/* Save bar skeleton */}
        <div className="h-12 border-t border-border px-4 flex items-center justify-end">
          <Skeleton className="h-7 w-20" />
        </div>
      </div>
    );
  }

  const relativeTime = formatDistanceToNow(new Date(file.updated_at), { addSuffix: true });
  const author = authorName ?? shortUuid(file.updated_by);

  return (
    <>
      <Card className="flex flex-col h-full rounded-none border-0 border-r border-border">
        {/* Editor toolbar */}
        <div className="h-12 border-b border-border px-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-mono font-medium truncate">{file.name}</span>
            {isDirty && (
              <span className="text-warning text-sm flex-shrink-0" aria-label="Unsaved changes">
                ●
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Last edit: {author} · {relativeTime}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
              onClick={() => setDeleteConfirmOpen(true)}
              aria-label="Delete file"
              title="Delete this file"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <FrontmatterForm
            value={draftFrontmatter}
            kind={file.kind}
            onChange={handleFrontmatterChange}
            disabled={saveFile.isPending}
          />

          <div className="px-4 pb-4">
            <MarkdownBodyEditor
              value={draftBody}
              onChange={handleBodyChange}
              readOnly={saveFile.isPending}
            />
          </div>
        </div>

        {/* Save bar */}
        <SaveBar
          isDirty={isDirty}
          isSaving={saveFile.isPending}
          hasValidationErrors={validationErrors}
          onSave={handleSave}
          onOpenHistory={() => setHistoryOpen(true)}
        />
      </Card>

      <HistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        fileId={file.id}
        currentVersion={loadedVersion ?? file.current_version}
        isDirty={isDirty}
        currentFrontmatter={draftFrontmatter}
        currentBody={draftBody}
      />

      <StaleVersionDialog
        open={staleDialogOpen}
        onOpenChange={setStaleDialogOpen}
        otherAuthor={staleOtherAuthor}
        theirVersion={staleTheirVersion}
        myVersion={loadedVersion}
        myContent={{
          frontmatter: JSON.stringify(draftFrontmatter, null, 2),
          body: draftBody,
        }}
        theirContent={(() => {
          const freshRow = rows.find((r) => r.id === file.id);
          if (!freshRow) return undefined;
          return {
            frontmatter: JSON.stringify(freshRow.frontmatter, null, 2),
            body: freshRow.body,
          };
        })()}
        onReload={handleReload}
        onViewChanges={() => {
          // The dialog handles opening the diff view internally.
        }}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{file.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All child nodes will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={softDelete.isPending}
            >
              {softDelete.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
